// =============================================================================
// Funnel tracking — UTM/click-id attribution + analytics events
// -----------------------------------------------------------------------------
// No analytics library is installed in this project, so trackEvent() dispatches
// to whatever is present on `window` (GA4 `gtag`, Meta Pixel `fbq`, or a
// `dataLayer`) and otherwise no-ops. Wire up real analytics by loading those
// scripts in public/index.html — no code change needed here.
// =============================================================================

const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
];

const STORAGE_KEY = 'rt_funnel_attribution';

/**
 * Read UTM parameters + fbclid from the current URL and persist them for the
 * rest of the session. Call this on every funnel entry point — the first
 * non-empty capture wins, so a later page without params keeps the original.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return {};

  const existing = getAttribution();
  const params = new URLSearchParams(window.location.search);
  const captured = { ...existing };
  let foundNew = false;

  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value && !captured[key]) {
      captured[key] = value;
      foundNew = true;
    }
  });

  if (foundNew || !captured._capturedAt) {
    captured._capturedAt = captured._capturedAt || new Date().toISOString();
    captured.landingPath = captured.landingPath || window.location.pathname;
    captured.referrer = captured.referrer || document.referrer || null;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    } catch (e) {
      /* sessionStorage unavailable (private mode) — ignore */
    }
  }

  return captured;
}

/** Get the attribution data captured earlier this session. */
export function getAttribution() {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

/** Return only the UTM/fbclid fields, normalised to null when missing. */
export function getUtmParams() {
  const a = getAttribution();
  return ATTRIBUTION_PARAMS.reduce((acc, key) => {
    acc[key] = a[key] || null;
    return acc;
  }, {});
}

/**
 * Build the attribution params attached to every analytics event. Returns only
 * the keys that have a value so we don't pollute GA4 with empty params.
 */
function attributionParams() {
  const a = getAttribution();
  const out = {};
  ATTRIBUTION_PARAMS.forEach((k) => {
    if (a[k]) out[k] = a[k];
  });
  if (a.landingPath) out.landing_path = a.landingPath;
  return out;
}

/**
 * Funnel events that also map to a Meta STANDARD event.
 *
 * Custom events (`trackCustom`) are invisible to Meta's optimisation and
 * conversion-tracking — the pixel only saw PageView, so ad delivery was
 * optimised for browsers rather than applicants. These two standard events are
 * what campaigns should optimise against:
 *   Lead                 — contact details captured (step 2 of the form)
 *   CompleteRegistration — application submitted and scored
 */
const META_STANDARD_EVENTS = {
  application_contact_captured: 'Lead',
  application_completed: 'CompleteRegistration',
};

/**
 * Fire a funnel analytics event. UTM/attribution params captured earlier in the
 * session are auto-merged in so GA4 reports can be sliced by ad campaign.
 *
 * Known events: page_view | cta_apply_click | application_started
 *   | application_step_viewed | application_step_completed
 *   | application_step_invalid | application_contact_captured
 *   | application_completed | application_submit_failed
 *   | qualified_result_viewed | nurture_result_viewed | not_fit_result_viewed
 */
export function trackEvent(event, payload = {}) {
  const enriched = { ...attributionParams(), ...payload };
  if (typeof window !== 'undefined') {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event, ...enriched });
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', event, enriched);
    }
    if (typeof window.fbq === 'function') {
      // Always send the custom event so the full funnel is visible in Meta's
      // event log, then the standard event Meta can actually optimise for.
      window.fbq('trackCustom', event, enriched);
      const standard = META_STANDARD_EVENTS[event];
      if (standard) {
        window.fbq('track', standard, enriched);
      }
    }
  }
  if (process.env.NODE_ENV !== 'production') {
    // Visible in dev so you can confirm events fire before analytics is wired up.
    // eslint-disable-next-line no-console
    console.info('[funnel:event]', event, enriched);
  }
}

/**
 * Fire a GA4 `page_view` for the current SPA route. Call from a router listener
 * — gtag's automatic page_view is disabled in public/index.html because it only
 * fires on the initial document load and misses React Router transitions.
 */
export function trackPageView(path, title) {
  if (typeof window === 'undefined') return;
  const page_path = path || window.location.pathname + window.location.search;
  const page_location = window.location.href;
  const page_title = title || document.title;
  trackEvent('page_view', { page_path, page_location, page_title });
}
